import React, { createContext, memo, useContext, useMemo } from 'react';
import {
  IconDocument,
  IconDocumentPdf,
  IconDocumentWord,
  IconDocumentExcel,
  IconDocumentPowerpoint,
  IconImage,
} from '../../icons';
import cn from '../../utils/cn';
import invariant from 'ts-invariant';
import FilePreviewActions from './FilePreviewActions';
import ImagePrimitive from '../image/Image';

export enum FilePreviewVariant {
  DOCUMENT = 'DOCUMENT',
  MEDIA = 'MEDIA',
}

type FilePreviewContextBase = {
  fileSrc: string;
  onRemove?: () => void;
};

type FilePreviewFileContext = FilePreviewContextBase & {
  variant: FilePreviewVariant.DOCUMENT;
  mimeType: string;
  fileName: string;
};

type FilePreviewImageContext = FilePreviewContextBase & {
  variant: FilePreviewVariant.MEDIA;
  mimeType?: string;
  fileName?: string;
};

type FilePreviewContextType = FilePreviewFileContext | FilePreviewImageContext;

const FilePreviewContext = createContext<FilePreviewContextType | undefined>(undefined);

export const useFilePreview = (): FilePreviewContextType => {
  const context = useContext(FilePreviewContext);

  invariant(context, 'useFilePreview must be used within a FilePreview component');

  return context;
};

type RootPropsBase = React.ComponentPropsWithoutRef<'div'> & FilePreviewContextBase;

/**
 * Props for document file preview.
 * When variant is omitted, defaults to DOCUMENT and requires mimeType and fileName.
 */
type FileRootProps = RootPropsBase & {
  variant?: FilePreviewVariant.DOCUMENT;
  mimeType: string;
  fileName: string;
};

/**
 * Props for media/image preview.
 * Variant must be explicitly set to MEDIA. fileName and mimeType are optional.
 */
type ImageRootProps = RootPropsBase & {
  variant: FilePreviewVariant.MEDIA;
  mimeType?: string;
  fileName?: string;
};

/**
 * Discriminated union of file and image preview props.
 * TypeScript narrows the type based on the `variant` property.
 */
type RootProps = FileRootProps | ImageRootProps;

/**
 * Root component for file preview.
 *
 * Note: We avoid destructuring variant-dependent props (mimeType, fileName) directly
 * in the function signature. Destructuring breaks TypeScript's discriminated union
 * narrowing — the compiler loses the link between `variant` and the other props.
 * By accessing props.mimeType and props.fileName after checking props.variant,
 * TypeScript can correctly infer their types.
 */
const Root = memo((props: RootProps) => {
  const {
    fileSrc,
    onRemove,
    className,
    variant = FilePreviewVariant.DOCUMENT,
    // Extracted to prevent passing non-DOM props to the div element
    mimeType, // eslint-disable-line @typescript-eslint/no-unused-vars
    fileName, // eslint-disable-line @typescript-eslint/no-unused-vars
    ...rest
  } = props;

  const contextValue = useMemo((): FilePreviewContextType => {
    const base = {
      fileSrc,
      onRemove,
    };

    if (props.variant === FilePreviewVariant.MEDIA) {
      return {
        ...base,
        variant: FilePreviewVariant.MEDIA,
        fileName: props.fileName,
      };
    }

    return {
      ...base,
      variant: FilePreviewVariant.DOCUMENT,
      mimeType: props.mimeType,
      fileName: props.fileName,
    };
  }, [fileSrc, onRemove, props.fileName, props.mimeType, props.variant]);

  return (
    <FilePreviewContext.Provider value={contextValue}>
      <div
        className={cn(
          'shrink-0 border border-neutral-300',
          {
            'flex items-center gap-5 rounded-lg p-5':
              variant === FilePreviewVariant.DOCUMENT,
            'relative size-full overflow-hidden rounded-xl':
              variant === FilePreviewVariant.MEDIA,
          },
          className,
        )}
        {...rest}
      />
    </FilePreviewContext.Provider>
  );
});

Root.displayName = 'FilePreview.Root';

const Icon = memo(({ className, ...props }: React.ComponentPropsWithoutRef<'svg'>) => {
  const { variant, mimeType } = useFilePreview();

  if (variant !== FilePreviewVariant.DOCUMENT) {
    return null;
  }

  let IconComponent: React.ComponentType<React.ComponentPropsWithoutRef<'svg'>> =
    IconDocument;
  let colorClass = 'text-neutral-400';

  if (mimeType.startsWith('image/')) {
    IconComponent = IconImage;
    colorClass = 'text-neutral-700';
  } else {
    switch (mimeType) {
      case 'application/pdf':
        IconComponent = IconDocumentPdf;
        colorClass = 'text-red-600';
        break;

      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        IconComponent = IconDocumentWord;
        colorClass = 'text-blue-600';
        break;

      case 'application/vnd.ms-excel':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        IconComponent = IconDocumentExcel;
        colorClass = 'text-green-600';
        break;

      case 'application/vnd.ms-powerpoint':
      case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        IconComponent = IconDocumentPowerpoint;
        colorClass = 'text-orange-400';
        break;
    }
  }

  return <IconComponent className={cn('size-6', colorClass, className)} {...props} />;
});

Icon.displayName = 'FilePreview.Icon';

const Message = memo(
  ({ className, ...props }: Omit<React.ComponentPropsWithoutRef<'p'>, 'children'>) => {
    const { variant, fileName } = useFilePreview();

    if (variant !== FilePreviewVariant.DOCUMENT) {
      return null;
    }

    return (
      <p
        className={cn('line-clamp-1 break-all text-xs text-neutral-700', className)}
        {...props}
      >
        {fileName}
      </p>
    );
  },
);

Message.displayName = 'FilePreview.Message';

const Image = memo(
  ({
    ...props
  }: Omit<React.ComponentPropsWithoutRef<typeof ImagePrimitive>, 'src' | 'alt'>) => {
    const { variant, fileName, fileSrc } = useFilePreview();

    if (variant !== FilePreviewVariant.MEDIA) {
      return null;
    }

    return <ImagePrimitive src={fileSrc} alt={fileName} {...props} />;
  },
);

Image.displayName = 'FilePreview.Image';

const FilePreview = memo((props: RootProps) => {
  if (props.variant === FilePreviewVariant.MEDIA) {
    return (
      <Root {...props}>
        <Image />

        <div className="absolute right-2.5 top-2.5">
          <FilePreviewActions className="flex-col" />
        </div>
      </Root>
    );
  }

  return (
    <Root {...props}>
      <div className="flex flex-1 items-center gap-2">
        <Icon />
        <Message />
      </div>

      <FilePreviewActions />
    </Root>
  );
});

FilePreview.displayName = 'FilePreview';

const FilePreviewCompound = Object.assign(FilePreview, {
  Root,
  Icon,
  Message,
  Image,
  Actions: FilePreviewActions,
});

export default FilePreviewCompound;
